/**
 * M7 S32: the Logs screen's view model (spec §2.4–§2.6). Pure: the Career's story block plus the story data in,
 * a `LogsView` out. The screen renders it as it is; every rule lives here.
 *
 * - **Tabs by source, not by thread** (the player sorts the case): *The case* first (the Case line, open leads, open
 *   questions), then one tab per log source that holds a log, then *Messages* (the S11 `log` callbacks: texture,
 *   never evidence), then *Your records*. A tab with an unopened log carries an unread mark (a word, never a count).
 * - **Entries:** every log in the ledger (unread first, newest first; then read, last read first) and every
 *   repeatable corroboration taken (`<template>#<n>`, spec §2.5; the meter's are in Your records). Thread, link,
 *   kind and weight are never shown (the player does the arithmetic). Template tokens (`{pilot}`) come from the
 *   story link's `vars`; one it can't fill reads as `tokenFallback`.
 * - **Open leads** (the ledger's `leads`) offer only *set nav target*. **Open questions** come from `openQuestions`
 *   (claims.json): a question shows once a read log raised it and closes when its tier's claim lands.
 * - **The Case line** is the word for `caseStrength` (the best bundle the player could pick now), never a number.
 * - **Guardian wrecks** read as raider wrecks until tier 3, like the kill log (spec §2.4).
 */
import type { Career } from '../../../career/types'
import { instanceCount, instanceId, type CaseData } from '../../../story/bundle'
import { caseStrength } from '../../../story/case'
import { currentTier, openQuestions, type ClaimData } from '../../../story/claims'
import type { EvidenceLog } from '../../../story/schemas'
import { storyOf } from '../../../story/state'
import { CASE_LINKS, type CaseLine, type LogSource } from '../../../story/types'
import type { LogsConfig } from './config'
import { yourRecords, type LogsNames, type RecordsView, type RecordsExtras } from './records'

/** The tier from which a guardian is called a guardian on this screen (spec §2.4). */
export const GUARDIAN_NAMED_TIER = 3

export type LogsData = CaseData & ClaimData

/** An S11 `log` callback the story link kept (texture; never evidence, never a bundle entry). */
export interface LogMessage {
  id: string
  text: string
  /** Speaker display name, when the callback has one. */
  from?: string
}

export interface LogsInput extends RecordsExtras {
  career: Career
  data: LogsData
  config: LogsConfig
  names: LogsNames
  /** Token values for a log or instance id (`{pilot}`, `{day}` ...); the story link fills them (S30). */
  vars?: (logId: string) => Readonly<Record<string, string>> | undefined
  /** The `log` callbacks the story link kept, oldest first. */
  messages?: readonly LogMessage[]
  /**
   * S37: a log's table rows as the story link generates them (Watch book 7's d121 row, the Watch rows from the
   * player's sales); undefined = the data's rows. Template tokens still fill.
   */
  rows?: (logId: string) => readonly (readonly string[])[] | undefined
}

export interface LogTable {
  columns: readonly string[]
  rows: readonly (readonly string[])[]
}

export interface LogEntryView {
  /** Ledger id: a log id or a repeatable instance `<template>#<n>`. */
  id: string
  title: string
  date: string
  sourceLabel: string
  body: string
  table: LogTable | null
  unread: boolean
  /** The log's lead is still open. */
  leadOpen: boolean
}

export type LogsTabId = 'case' | 'records' | 'messages' | `source:${LogSource}`

export interface LogsTab {
  id: LogsTabId
  label: string
  unread: boolean
}

export interface LeadRow {
  logId: string
  title: string
  /** "Points to a derelict" (the poi kind only; the story link resolves the place). */
  pointsTo: string
}

export interface QuestionRow {
  id: string
  text: string
}

export interface CaseLineView {
  line: CaseLine
  word: string
  hint: string
}

export interface LogsView {
  title: string
  caseLine: CaseLineView
  tabs: readonly LogsTab[]
  /** Entries per source tab and the Messages tab. */
  entries: Readonly<Partial<Record<LogsTabId, readonly LogEntryView[]>>>
  leads: readonly LeadRow[]
  questions: readonly QuestionRow[]
  records: RecordsView
  /** Nothing logged at all (the case tab shows `empty.screen`). */
  empty: boolean
  /** M7 S34: the claim draft row on the case tab (its label), when a claim can be drafted; the host sets it. */
  claimDraft?: string | null
  /** M7 S39: the finale row on the case tab (its label), while a finale path is on offer; the host sets it. */
  finaleRow?: string | null
}

const TOKEN = /\{([a-zA-Z]+)\}/g

/** `text` with its `{token}`s filled from `vars`; an unknown token reads as `fallback`. */
export function fillTokens(text: string, vars: Readonly<Record<string, string>> | undefined, fallback: string): string {
  return text.replace(TOKEN, (_m, key: string) => vars?.[key] ?? fallback)
}

/** The source tab's label (guardian wrecks are raider wrecks until tier 3). */
export function sourceLabel(source: LogSource, tier: number, cfg: LogsConfig): string {
  if (source === 'guardianWreck' && tier < GUARDIAN_NAMED_TIER) return cfg.sources.guardianWreckEarly
  return cfg.sources.labels[source]
}

function entryOf(
  id: string,
  log: EvidenceLog,
  unread: boolean,
  leadOpen: boolean,
  tier: number,
  input: LogsInput,
): LogEntryView {
  const fb = input.config.tokenFallback
  const vars = log.template ? input.vars?.(id) : undefined
  const fill = (s: string): string => (log.template ? fillTokens(s, vars, fb) : s)
  return {
    id,
    title: fill(log.title),
    date: fill(log.date),
    sourceLabel: sourceLabel(log.source, tier, input.config),
    body: fill(log.body),
    table:
      log.table === undefined ? null : { columns: log.table.columns.map(fill), rows: (input.rows?.(id) ?? log.table.rows).map((r) => r.map(fill)) },
    unread,
    leadOpen,
  }
}

interface Ledgered {
  id: string
  log: EvidenceLog
  unread: boolean
}

/** Every ledger entry: unread first (newest first), then read (last read first), then corroborations taken. */
function ledger(career: Career, data: LogsData): Ledgered[] {
  const s = storyOf(career)
  const out: Ledgered[] = []
  const push = (id: string, unread: boolean): void => {
    const log = data.logsById.get(id)
    if (log !== undefined) out.push({ id, log, unread })
  }
  for (let i = s.unreadLogs.length - 1; i >= 0; i--) push(s.unreadLogs[i], true)
  for (let i = s.readLogs.length - 1; i >= 0; i--) push(s.readLogs[i], false)
  for (const link of CASE_LINKS) {
    if (link === 'knowledge') continue // meter proofs: Your records
    const template = data.case.corroboration.templates[link]
    const log = data.logsById.get(template)
    if (log === undefined) continue
    const n = instanceCount(career, link)
    for (let i = n; i >= 1; i--) out.push({ id: instanceId(template, i), log, unread: false })
  }
  return out
}

export function caseLineView(career: Career, data: LogsData, cfg: LogsConfig): CaseLineView {
  const { line } = caseStrength(career, data)
  return { line, word: cfg.caseLine.words[line], hint: cfg.caseLine.hints[line] }
}

export function leadRows(career: Career, data: LogsData, cfg: LogsConfig): LeadRow[] {
  const out: LeadRow[] = []
  for (const id of storyOf(career).leads) {
    const log = data.logsById.get(id)
    if (log?.lead === undefined) continue
    out.push({ logId: id, title: log.title, pointsTo: `${cfg.leads.pointsTo} ${cfg.leads.poiKinds[log.lead.poiKind]}` })
  }
  return out
}

export function questionRows(career: Career, data: LogsData): QuestionRow[] {
  return openQuestions(career, data).map((q) => ({ id: q.id, text: q.text }))
}

export function buildLogsView(input: LogsInput): LogsView {
  const { career, data, config: cfg } = input
  const tier = currentTier(career)
  const leadsOpen = new Set(storyOf(career).leads)
  const bySource = new Map<LogSource, LogEntryView[]>()
  const recordLogs: LogEntryView[] = []
  for (const e of ledger(career, data)) {
    const view = entryOf(e.id, e.log, e.unread, leadsOpen.has(e.id), tier, input)
    if (e.log.source === 'yourRecords') {
      recordLogs.push(view)
      continue
    }
    const list = bySource.get(e.log.source) ?? []
    list.push(view)
    bySource.set(e.log.source, list)
  }

  const tabs: LogsTab[] = [{ id: 'case', label: cfg.tabs.case, unread: false }]
  const entries: Partial<Record<LogsTabId, readonly LogEntryView[]>> = {}
  for (const source of cfg.sources.order as readonly LogSource[]) {
    const list = bySource.get(source)
    if (list === undefined) continue
    const id: LogsTabId = `source:${source}`
    tabs.push({ id, label: sourceLabel(source, tier, cfg), unread: list.some((e) => e.unread) })
    entries[id] = list
  }
  const messages = input.messages ?? []
  if (messages.length > 0) {
    tabs.push({ id: 'messages', label: cfg.tabs.messages, unread: false })
    entries.messages = [...messages].reverse().map((m) => ({
      id: m.id,
      title: m.from ?? cfg.tabs.messages,
      date: '',
      sourceLabel: cfg.tabs.messages,
      body: m.text,
      table: null,
      unread: false,
      leadOpen: false,
    }))
  }
  const records = yourRecords(career, cfg, input.names, input, recordLogs)
  tabs.push({ id: 'records', label: cfg.tabs.records, unread: recordLogs.some((e) => e.unread) })

  return {
    title: cfg.title,
    caseLine: caseLineView(career, data, cfg),
    tabs,
    entries,
    leads: leadRows(career, data, cfg),
    questions: questionRows(career, data),
    records,
    empty: tabs.length === 2 && records.empty,
  }
}
